import { useEffect, useMemo, useState } from "react";
import * as Accordion from "@radix-ui/react-accordion";
import { Check, ChevronDown } from "lucide-react";
import { API } from "@/lib/api";
import { CHECKLIST } from "@/data/checklist";
import { SectionHead } from "@/components/SectionHead";
import { Reveal } from "@/components/Reveal";

export function Checklist() {
  const [categories, setCategories] = useState(CHECKLIST || []);
  const [checked, setChecked] = useState({});

  useEffect(() => {
    API.get("/checklist").then((r) => {
      if (r?.data?.categories?.length) setCategories(r.data.categories);
    }).catch(() => {});
  }, []);

  const total = useMemo(() => categories.reduce((n, c) => n + c.items.length, 0), [categories]);
  const done = Object.keys(checked).length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  const toggle = (key) => setChecked((p) => {
    const n = { ...p };
    if (n[key]) delete n[key]; else n[key] = true;
    return n;
  });

  return (
    <section id="seo-checklist" className="py-16 sm:py-24 lg:py-32 bg-rw-bg2 border-y border-rw-border" data-testid="checklist-section">
      <div className="container-rw">
        <SectionHead
          eyebrow="Full Transparency"
          title="The Complete SEO Checklist"
          description="This is the process we run — all 211 checks across nine disciplines. Expand a category, tick items your website already passes, and see exactly where the gaps are."
          testid="checklist-head"
        />

        <Reveal className="mt-10">
          <div className="rounded-xl border border-rw-border bg-rw-card p-5" data-testid="checklist-progress">
            <div className="flex items-center justify-between font-mono text-xs">
              <span className="uppercase tracking-[0.2em] text-rw-muted">Your coverage</span>
              <span className="text-rw-green" data-testid="checklist-progress-text">{done} / {total} · {pct}%</span>
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-rw-bg">
              <div className="h-full rounded-full bg-rw-green transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </Reveal>

        <Reveal className="mt-8">
          <Accordion.Root type="multiple" className="space-y-3">
            {categories.map((cat, ci) => (
              <Accordion.Item key={cat.id} value={cat.id}
                className="overflow-hidden rounded-xl border border-rw-border bg-rw-card data-[state=open]:border-rw-green/40"
                data-testid={`checklist-category-${cat.id}`}>
                <Accordion.Trigger
                  className="group flex w-full items-center justify-between px-5 sm:px-7 py-5 text-left"
                  data-testid={`checklist-toggle-${cat.id}`}>
                  <span className="flex items-center gap-4">
                    <span className="font-mono text-xs text-rw-green">0{ci + 1}</span>
                    <span className="font-jakarta text-base sm:text-lg font-bold text-rw-text">{cat.name}</span>
                    <span className="rounded-full border border-rw-border px-2.5 py-0.5 font-mono text-[10px] text-rw-dim">{cat.items.length} checks</span>
                  </span>
                  <ChevronDown size={18} className="shrink-0 text-rw-muted transition-transform duration-300 group-data-[state=open]:rotate-180" />
                </Accordion.Trigger>
                <Accordion.Content className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
                  <ul className="grid gap-x-6 gap-y-1 px-5 sm:px-7 pb-6 sm:grid-cols-2 lg:grid-cols-3">
                    {cat.items.map((item) => {
                      const key = `${cat.id}:${item}`;
                      const on = !!checked[key];
                      return (
                        <li key={item}>
                          <button
                            type="button"
                            onClick={() => toggle(key)}
                            aria-pressed={on}
                            data-testid={`checklist-item-${cat.id}-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm text-rw-muted hover:text-rw-text transition-colors"
                          >
                            <span className={`flex h-4.5 w-4.5 h-[18px] w-[18px] shrink-0 items-center justify-center rounded border transition-colors ${on ? "border-rw-green bg-rw-green text-rw-bg" : "border-rw-border2"}`}>
                              {on && <Check size={12} strokeWidth={3} />}
                            </span>
                            <span className={on ? "text-rw-text" : ""}>{item}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </Accordion.Content>
              </Accordion.Item>
            ))}
          </Accordion.Root>
        </Reveal>
      </div>
    </section>
  );
}
